import { Box, Typography } from '@mui/material';
import React from 'react';

import { PageNumber } from '@common/components/PageNumber';
import { PageNumberListProps } from '@common/types/campaign';
import {
  CONTROL_SIZE,
  GAP_BETWEEN_ELEMENTS,
  getPageChangeHandler,
} from '@common/utils/campaignPaginationItems';
/**
 * PageNumberList
 *
 * Renders a list of page numbers and ellipsis based on the provided pagination items.
 *
 * @param props - The properties for the component
 * @param props.clampedPage - the current page, clamped to the valid range
 * @param props.isPageChangeDisabled - whether page changes are currently disabled
 * @param props.listKey - a unique key for the list (e.g., 'desktop' or 'mobile')
 * @param props.pageItems - the array of pagination items to render
 * @param props.runPageChange - the function to execute when a page is selected
 * @param props.sx - optional MUI styling properties
 */
export function PageNumberList({
  clampedPage,
  isPageChangeDisabled,
  listKey,
  pageItems,
  runPageChange,
  sx,
}: PageNumberListProps) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: GAP_BETWEEN_ELEMENTS,
        marginX: GAP_BETWEEN_ELEMENTS,
        ...sx,
      }}
    >
      {pageItems.map((item) => {
        if (item.type === 'start-ellipsis' || item.type === 'end-ellipsis') {
          return (
            <Typography
              key={`${listKey}-${item.type}`}
              aria-hidden
              component="span"
              sx={{
                alignItems: 'center',
                display: 'inline-flex',
                height: CONTROL_SIZE,
                justifyContent: 'center',
                px: '4px',
                userSelect: 'none',
              }}
            >
              …
            </Typography>
          );
        }

        if (item.type !== 'page') {
          return null;
        }

        return (
          <PageNumber
            key={`${listKey}-${item.page}`}
            disabled={item.disabled}
            page={item.page}
            selected={item.selected}
            onClick={getPageChangeHandler(
              item,
              clampedPage,
              isPageChangeDisabled,
              runPageChange
            )}
          />
        );
      })}
    </Box>
  );
}
